import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

type LP = any;

export default function AdminLandingPages() {
  const [rows, setRows] = useState<LP[]>([]);
  const [promos, setPromos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<LP | null>(null);

  async function load() {
    setLoading(true);
    const [{ data: pages }, { data: ps }] = await Promise.all([
      supabase.from("landing_pages").select("*").order("created_at", { ascending: false }),
      supabase.from("promo_codes").select("id,code").order("code"),
    ]);
    setRows(pages ?? []);
    setPromos(ps ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!edit?.slug || !edit?.title) return toast.error("Slug + title required");
    const payload = { ...edit, slug: edit.slug.toLowerCase().replace(/[^a-z0-9-]/g, "-") };
    const { error } = edit.id
      ? await supabase.from("landing_pages").update(payload).eq("id", edit.id)
      : await supabase.from("landing_pages").insert(payload);
    if (error) return toast.error(error.message);
    toast.success("Saved"); setEdit(null); load();
  }

  async function del(id: string) {
    if (!confirm("Delete landing page?")) return;
    await supabase.from("landing_pages").delete().eq("id", id);
    load();
  }

  return (
    <AdminShell title="Landing Pages" actions={<Button size="sm" onClick={() => setEdit({ published: false, form_fields: [{ name: "full_name", label: "Name", type: "text", required: true }, { name: "email", label: "Email", type: "email", required: true }, { name: "phone", label: "Phone", type: "tel", required: false }] })}><Plus className="w-4 h-4 mr-1" /> New Page</Button>}>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <div className="bg-surface border border-border">
          <table className="w-full text-sm">
            <thead className="bg-panel"><tr><th className="text-left p-2">Slug</th><th className="text-left p-2">Title</th><th className="text-left p-2">Offer</th><th className="text-left p-2">Status</th><th></th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-2 font-mono">/p/{r.slug}</td>
                  <td className="p-2">{r.title}</td>
                  <td className="p-2 font-mono text-xs">{promos.find(p => p.id === r.offer_promo_code_id)?.code ?? "—"}</td>
                  <td className="p-2">{r.published ? "Published" : "Draft"}</td>
                  <td className="p-2 flex gap-1">
                    {r.published && <Link to={`/p/${r.slug}`} target="_blank"><Button variant="ghost" size="icon"><ExternalLink className="w-4 h-4" /></Button></Link>}
                    <Button variant="ghost" size="icon" onClick={() => setEdit(r)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r.id)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No landing pages yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{edit.id ? "Edit" : "New"} Landing Page</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Slug</Label><Input value={edit.slug ?? ""} onChange={e => setEdit({ ...edit, slug: e.target.value })} /></div>
              <div><Label>Title</Label><Input value={edit.title ?? ""} onChange={e => setEdit({ ...edit, title: e.target.value })} /></div>
              <div className="col-span-2"><Label>Headline</Label><Input value={edit.headline ?? ""} onChange={e => setEdit({ ...edit, headline: e.target.value })} /></div>
              <div className="col-span-2"><Label>Subheadline</Label><Input value={edit.subheadline ?? ""} onChange={e => setEdit({ ...edit, subheadline: e.target.value })} /></div>
              <div className="col-span-2"><Label>Hero image URL</Label><Input value={edit.hero_image ?? ""} onChange={e => setEdit({ ...edit, hero_image: e.target.value })} /></div>
              <div className="col-span-2"><Label>Body (markdown)</Label><Textarea rows={6} value={edit.body_md ?? ""} onChange={e => setEdit({ ...edit, body_md: e.target.value })} /></div>
              <div><Label>CTA label</Label><Input value={edit.cta_label ?? ""} onChange={e => setEdit({ ...edit, cta_label: e.target.value })} /></div>
              <div><Label>Offer (promo)</Label>
                <Select value={edit.offer_promo_code_id ?? "none"} onValueChange={v => setEdit({ ...edit, offer_promo_code_id: v === "none" ? null : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— none —</SelectItem>
                    {promos.map(p => <SelectItem key={p.id} value={p.id}>{p.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Success message</Label><Input value={edit.success_message ?? ""} onChange={e => setEdit({ ...edit, success_message: e.target.value })} /></div>
              <div><Label>SEO title</Label><Input value={edit.seo_title ?? ""} onChange={e => setEdit({ ...edit, seo_title: e.target.value })} /></div>
              <div><Label>SEO description</Label><Input value={edit.seo_description ?? ""} onChange={e => setEdit({ ...edit, seo_description: e.target.value })} /></div>
              <div className="col-span-2 flex items-center gap-2"><Switch checked={!!edit.published} onCheckedChange={(v) => setEdit({ ...edit, published: v })} /> <Label>Published</Label></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              <Button onClick={save}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AdminShell>
  );
}